import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const sanitizeExcelValue = (value) => {
  if (typeof value !== "string") return value;
  if (/^[@=+\-\t\r]/.test(value)) return `'${value}`;
  return value;
};

const buildTimestamp = () => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, "0");
  const d = String(now.getDate()).padStart(2, "0");
  const hh = String(now.getHours()).padStart(2, "0");
  const mm = String(now.getMinutes()).padStart(2, "0");
  return `${y}${m}${d}_${hh}${mm}`;
};

const buildFileName = (reportName, dossierId, ext = "xlsx") =>
  `${reportName}_dossier_${dossierId || "NA"}_${buildTimestamp()}.${ext}`;

const writeWorkbook = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ sheetName, rows }) => {
    const ws = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });
  XLSX.writeFile(wb, filename, { bookType: "xlsx" });
};

// Écrit une feuille construite en tableau de lignes (en-têtes fusionnées possibles)
const writeWorkbookAoa = (sheets, filename) => {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ sheetName, aoa, merges = [], cols }) => {
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    if (merges.length) ws["!merges"] = merges;
    if (cols) ws["!cols"] = cols;
    XLSX.utils.book_append_sheet(wb, ws, sheetName);
  });
  XLSX.writeFile(wb, filename, { bookType: "xlsx" });
};

// Tri comptable SYSCOHADA : d'abord la classe (1er chiffre), puis le numéro
// croissant à l'intérieur de la classe. Ex. : 101 < 401000 < 411000 < 521 < 5711.
// Robuste : tolère les chaînes et valeurs manquantes (codes non numériques en fin).
export const comparerComptes = (a, b) => {
  const sa = String(a ?? "").trim();
  const sb = String(b ?? "").trim();
  const ma = sa.match(/^\d+/);
  const mb = sb.match(/^\d+/);
  if (!ma || !mb) {
    if (ma) return -1;
    if (mb) return 1;
    return sa.localeCompare(sb, "fr", { numeric: true });
  }
  if (ma[0][0] !== mb[0][0]) return ma[0][0] < mb[0][0] ? -1 : 1;
  const na = parseInt(ma[0], 10);
  const nb = parseInt(mb[0], 10);
  if (na !== nb) return na - nb;
  return sa.localeCompare(sb, "fr", { numeric: true });
};

const trierComptes = (list, key = "numero_comptable") =>
  [...list].sort((a, b) => comparerComptes(a[key], b[key]));

const addPdfHeader = (doc, title, dossierId) => {
  doc.setFontSize(14);
  doc.setTextColor(0, 143, 160);
  doc.text(title, 14, 14);
  doc.setFontSize(9);
  doc.setTextColor(90, 90, 90);
  doc.text(`Dossier: ${dossierId || "NA"}`, 14, 20);
  doc.text(`Genere le: ${new Date().toLocaleString("fr-FR")}`, 14, 25);
};

const addPdfFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setTextColor(120, 120, 120);
    doc.setFont(undefined, 'italic');
    const footerText = "Généré avec EasyCompta";
    const textWidth = doc.getTextWidth(footerText);
    const xPosition = (pageWidth - textWidth) / 2;
    doc.text(footerText, xPosition, pageHeight - 10);
    doc.setFont(undefined, 'normal');
  }
};

const addExcelFooter = (rows) => {
  rows.push({});
  rows.push({ "": "Généré avec EasyCompta" });
};

const defaultPdfHeadStyle = {
  fillColor: [0, 143, 160],
  textColor: 255,
  fontStyle: "bold",
};

export const exportLivreJournalExcel = ({
  journal = [],
  totalDebits = 0,
  totalCredits = 0,
  equilibre = true,
  dossierId,
}) => {
  const rows = [];
  journal.forEach((ecriture) => {
    (ecriture.lignes || []).forEach((ligne) => {
      rows.push({
        Date: sanitizeExcelValue(ecriture.date || ""),
        "Numero ecriture": ecriture.numero_ecriture || "",
        "Libelle global": sanitizeExcelValue(ecriture.libelle_global || ""),
        Type: sanitizeExcelValue(ligne.type || ""),
        Compte: sanitizeExcelValue(ligne.code || ""),
        Intitule: sanitizeExcelValue(ligne.intitule || ""),
        Debit: ligne.debit || 0,
        Credit: ligne.credit || 0,
      });
    });
  });
  
  addExcelFooter(rows);

  writeWorkbook(
    [
      { sheetName: "Journal", rows },
      {
        sheetName: "Resume",
        rows: [
          {
            "Total debits": totalDebits,
            "Total credits": totalCredits,
            Equilibre: equilibre ? "Oui" : "Non",
            "Nombre ecritures": journal.length,
          },
        ],
      },
    ],
    buildFileName("livre_journal", dossierId),
  );
};

export const exportLivreJournalPdf = ({
  journal = [],
  totalDebits = 0,
  totalCredits = 0,
  equilibre = true,
  dossierId,
}) => {
  const doc = new jsPDF("landscape");
  addPdfHeader(doc, "Journal", dossierId);

  const body = [];
  journal.forEach((ecriture) => {
    (ecriture.lignes || []).forEach((ligne) => {
      body.push([
        ecriture.date || "",
        ecriture.numero_ecriture || "",
        ecriture.libelle_global || "",
        ligne.type || "",
        ligne.code || "",
        ligne.intitule || "",
        ligne.debit || 0,
        ligne.credit || 0,
      ]);
    });
  });

  autoTable(doc, {
    startY: 30,
    head: [[
      "Date", "Numero", "Libelle", "Type", "Compte", "Intitule", "Debit", "Credit",
    ]],
    body,
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  const finalY = doc.lastAutoTable?.finalY || 35;
  autoTable(doc, {
    startY: finalY + 6,
    head: [["Total debits", "Total credits", "Equilibre", "Nombre ecritures"]],
    body: [[totalDebits, totalCredits, equilibre ? "Oui" : "Non", journal.length]],
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  addPdfFooter(doc);
  doc.save(buildFileName("livre_journal", dossierId, "pdf"));
};

export const exportGrandLivreExcel = ({
  comptes = [],
  totalDebits = 0,
  totalCredits = 0,
  equilibre = true,
  dossierId,
}) => {
  const comptesTries = trierComptes(comptes);
  const rows = [];
  comptesTries.forEach((compte) => {
    (compte.mouvements || []).forEach((mouvement) => {
      rows.push({
        Classe: sanitizeExcelValue(compte.classe || ""),
        "Numero compte": sanitizeExcelValue(compte.numero_comptable || ""),
        "Intitule compte": sanitizeExcelValue(compte.intitule || ""),
        Date: sanitizeExcelValue(mouvement.date || ""),
        Libelle: sanitizeExcelValue(mouvement.libelle || ""),
        Contrepartie: sanitizeExcelValue(mouvement.contrepartie || ""),
        Debit: mouvement.debit || 0,
        Credit: mouvement.credit || 0,
      });
    });
  });
  
  addExcelFooter(rows);

  const resume = comptesTries.map((compte) => ({
    Classe: sanitizeExcelValue(compte.classe || ""),
    "Numero compte": sanitizeExcelValue(compte.numero_comptable || ""),
    "Intitule compte": sanitizeExcelValue(compte.intitule || ""),
    "Total debit": compte.total_debit || 0,
    "Total credit": compte.total_credit || 0,
    "Solde debiteur": compte.solde_debiteur || 0,
    "Solde crediteur": compte.solde_crediteur || 0,
    "Nature solde": sanitizeExcelValue(compte.nature_solde || "NUL"),
  }));

  writeWorkbook(
    [
      { sheetName: "Grand Livre", rows },
      { sheetName: "Resume Comptes", rows: resume },
      {
        sheetName: "Totaux",
        rows: [
          {
            "Total debits": totalDebits,
            "Total credits": totalCredits,
            Equilibre: equilibre ? "Oui" : "Non",
            "Nombre comptes": comptesTries.length,
          },
        ],
      },
    ],
    buildFileName("grand_livre", dossierId),
  );
};

export const exportGrandLivrePdf = ({
  comptes = [],
  totalDebits = 0,
  totalCredits = 0,
  equilibre = true,
  dossierId,
}) => {
  const comptesTries = trierComptes(comptes);
  const doc = new jsPDF("landscape");
  addPdfHeader(doc, "Grand Livre", dossierId);

  const body = [];
  comptesTries.forEach((compte) => {
    (compte.mouvements || []).forEach((mv) => {
      body.push([
        compte.classe || "",
        compte.numero_comptable || "",
        compte.intitule || "",
        mv.date || "",
        mv.libelle || "",
        mv.contrepartie || "",
        mv.debit || 0,
        mv.credit || 0,
      ]);
    });
  });

  autoTable(doc, {
    startY: 30,
    head: [[
      "Classe", "Numero compte", "Intitule", "Date", "Libelle", "Contrepartie", "Debit", "Credit",
    ]],
    body,
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  const finalY = doc.lastAutoTable?.finalY || 35;
  autoTable(doc, {
    startY: finalY + 6,
    head: [["Total debits", "Total credits", "Equilibre", "Nombre comptes"]],
    body: [[totalDebits, totalCredits, equilibre ? "Oui" : "Non", comptesTries.length]],
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  addPdfFooter(doc);
  doc.save(buildFileName("grand_livre", dossierId, "pdf"));
};

export const exportBalanceExcel = ({
  balance = [],
  totalDebits = 0,
  totalCredits = 0,
  totalDebiteur = 0,
  totalCrediteur = 0,
  equilibre = true,
  nombreComptes = 0,
  dossierId,
}) => {
  const rows = trierComptes(balance).map((line) => ({
    "Numero compte": sanitizeExcelValue(line.numero_comptable || ""),
    Libelle: sanitizeExcelValue(line.libelle || ""),
    Debit: line.debit || 0,
    Credit: line.credit || 0,
    "Solde debiteur": line.solde_debiteur || 0,
    "Solde crediteur": line.solde_crediteur || 0,
    "Nature solde": sanitizeExcelValue(line.nature_solde || ""),
  }));
  
  addExcelFooter(rows);

  writeWorkbook(
    [
      { sheetName: "Balance Generale", rows },
      {
        sheetName: "Totaux",
        rows: [
          {
            "Total mouvements debit": totalDebits,
            "Total mouvements credit": totalCredits,
            "Total soldes debiteurs": totalDebiteur,
            "Total soldes crediteurs": totalCrediteur,
            Equilibre: equilibre ? "Oui" : "Non",
            "Nombre comptes": nombreComptes,
          },
        ],
      },
    ],
    buildFileName("balance_generale", dossierId),
  );
};

/**
 * Balance à 6 colonnes (format demandé par CECA) :
 *   N° compte | Intitulé | Ouverture D | Ouverture C | Mvt D | Mvt C | Cumulé D | Cumulé C
 *   « Ouverture » = solde à nouveau (écritures avant la période filtrée)
 *   « Soldes cumulés » = ouverture + mouvements de la période
 */
export const exportBalance6Excel = ({
  balance = [],
  totalOuvertureDebit = 0,
  totalOuvertureCredit = 0,
  totalDebits = 0,
  totalCredits = 0,
  totalCumulDebiteur = 0,
  totalCumulCrediteur = 0,
  dossierId,
  dateDebut,
}) => {
  const labelOuverture = dateDebut
    ? `Mouvements au ${new Date(new Date(dateDebut).getTime() - 86400000).toLocaleDateString("fr-FR")}`
    : "Solde d'ouverture";

  const aoa = [
    ["N° de compte", "Intitulé des comptes", labelOuverture, "", "Mouvements", "", "Soldes cumulés", ""],
    ["", "", "Débit", "Crédit", "Débit", "Crédit", "Débit", "Crédit"],
  ];
  trierComptes(balance).forEach((l) => {
    aoa.push([
      l.numero_comptable || "",
      sanitizeExcelValue(l.libelle || ""),
      l.ouverture_debit || 0,
      l.ouverture_credit || 0,
      l.debit || 0,
      l.credit || 0,
      l.cumul_debiteur ?? l.solde_debiteur ?? 0,
      l.cumul_crediteur ?? l.solde_crediteur ?? 0,
    ]);
  });
  aoa.push([
    "TOTAUX", "",
    totalOuvertureDebit, totalOuvertureCredit,
    totalDebits, totalCredits,
    totalCumulDebiteur, totalCumulCrediteur,
  ]);
  aoa.push([]);
  aoa.push(["", "Généré avec EasyCompta"]);

  writeWorkbookAoa(
    [{
      sheetName: "Balance 6 colonnes",
      aoa,
      merges: [
        { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // N° compte
        { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // Intitulé
        { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } }, // Ouverture
        { s: { r: 0, c: 4 }, e: { r: 0, c: 5 } }, // Mouvements
        { s: { r: 0, c: 6 }, e: { r: 0, c: 7 } }, // Soldes cumulés
      ],
      cols: [
        { wch: 14 }, { wch: 34 },
        { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 }, { wch: 14 },
      ],
    }],
    buildFileName("balance_6_colonnes", dossierId),
  );
};

export const exportBalancePdf = ({
  balance = [],
  totalDebits = 0,
  totalCredits = 0,
  totalDebiteur = 0,
  totalCrediteur = 0,
  equilibre = true,
  nombreComptes = 0,
  dossierId,
}) => {
  const doc = new jsPDF("landscape");
  addPdfHeader(doc, "Balance Generale", dossierId);

  const body = trierComptes(balance).map((line) => ([
    line.numero_comptable || "",
    line.libelle || "",
    line.debit || 0,
    line.credit || 0,
    line.solde_debiteur || 0,
    line.solde_crediteur || 0,
    line.nature_solde || "",
  ]));

  autoTable(doc, {
    startY: 30,
    head: [[
      "Numero compte", "Libelle", "Debit", "Credit", "Solde debiteur", "Solde crediteur", "Nature",
    ]],
    body,
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  const finalY = doc.lastAutoTable?.finalY || 35;
  autoTable(doc, {
    startY: finalY + 6,
    head: [[
      "Total mvt debit",
      "Total mvt credit",
      "Total soldes debiteurs",
      "Total soldes crediteurs",
      "Equilibre",
      "Nombre comptes",
    ]],
    body: [[
      totalDebits,
      totalCredits,
      totalDebiteur,
      totalCrediteur,
      equilibre ? "Oui" : "Non",
      nombreComptes,
    ]],
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  addPdfFooter(doc);
  doc.save(buildFileName("balance_generale", dossierId, "pdf"));
};

export const exportCompteResultatExcel = ({
  lignes = [],
  resultatNet = 0,
  dossierId,
  annee,
}) => {
  const rows = lignes.map((line) => ({
    REF: sanitizeExcelValue(line.ref || ""),
    Libelle: sanitizeExcelValue(line.libelle || ""),
    Signe: sanitizeExcelValue(line.sign || ""),
    Note: sanitizeExcelValue(line.note || ""),
    [`Net ${annee}`]: line.netN || 0,
    [`Net ${annee - 1}`]: line.netN1 || 0,
    "Sous total": line.isSubtotal ? "Oui" : "Non",
  }));
  
  addExcelFooter(rows);

  writeWorkbook(
    [
      { sheetName: "Compte Resultat", rows },
      {
        sheetName: "Resume",
        rows: [{ "Resultat net": resultatNet, Exercice: annee }],
      },
    ],
    buildFileName("compte_resultat", dossierId),
  );
};

export const exportCompteResultatPdf = ({
  lignes = [],
  resultatNet = 0,
  dossierId,
  annee,
}) => {
  const doc = new jsPDF("landscape");
  addPdfHeader(doc, "Compte de Resultat", dossierId);

  const body = lignes.map((line) => ([
    line.ref || "",
    line.libelle || "",
    line.sign || "",
    line.note || "",
    line.netN || 0,
    line.netN1 || 0,
    line.isSubtotal ? "Oui" : "Non",
  ]));

  autoTable(doc, {
    startY: 30,
    head: [[
      "REF",
      "Libelle",
      "Signe",
      "Note",
      `Net ${annee}`,
      `Net ${annee - 1}`,
      "Sous total",
    ]],
    body,
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  const finalY = doc.lastAutoTable?.finalY || 35;
  autoTable(doc, {
    startY: finalY + 6,
    head: [["Resultat net", "Exercice"]],
    body: [[resultatNet, annee]],
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  addPdfFooter(doc);
  doc.save(buildFileName("compte_resultat", dossierId, "pdf"));
};

export const exportBilanComptableExcel = ({
  actif = [],
  passif = [],
  totalActif = 0,
  totalPassif = 0,
  equilibre = true,
  resultatNet = 0,
  dossierId,
}) => {
  const maxRows = Math.max(actif.length, passif.length);
  const rows = Array.from({ length: maxRows }).map((_, idx) => {
    const a = actif[idx] || {};
    const p = passif[idx] || {};
    return {
      "Actif REF": sanitizeExcelValue(a.ref || ""),
      "Actif Libelle": sanitizeExcelValue(a.label || ""),
      "Actif Brut": a.brut || 0,
      "Actif Amort": a.amortissement || 0,
      "Actif Net": a.net || 0,
      "Passif REF": sanitizeExcelValue(p.ref || ""),
      "Passif Libelle": sanitizeExcelValue(p.label || ""),
      "Passif Net": p.net || 0,
    };
  });
  
  addExcelFooter(rows);

  writeWorkbook(
    [
      { sheetName: "Bilan", rows },
      {
        sheetName: "Resume",
        rows: [
          {
            "Total actif": totalActif,
            "Total passif": totalPassif,
            Equilibre: equilibre ? "Oui" : "Non",
            "Resultat net": resultatNet || 0,
          },
        ],
      },
    ],
    buildFileName("bilan_comptable", dossierId),
  );
};

export const exportBilanComptablePdf = ({
  actif = [],
  passif = [],
  totalActif = 0,
  totalPassif = 0,
  equilibre = true,
  resultatNet = 0,
  dossierId,
}) => {
  const doc = new jsPDF("landscape");
  addPdfHeader(doc, "Bilan", dossierId);

  const maxRows = Math.max(actif.length, passif.length);
  const body = Array.from({ length: maxRows }).map((_, idx) => {
    const a = actif[idx] || {};
    const p = passif[idx] || {};
    return [
      a.ref || "",
      a.label || "",
      a.brut || 0,
      a.amortissement || 0,
      a.net || 0,
      p.ref || "",
      p.label || "",
      p.net || 0,
    ];
  });

  autoTable(doc, {
    startY: 30,
    head: [[
      "Actif REF",
      "Actif Libelle",
      "Actif Brut",
      "Actif Amort",
      "Actif Net",
      "Passif REF",
      "Passif Libelle",
      "Passif Net",
    ]],
    body,
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  const finalY = doc.lastAutoTable?.finalY || 35;
  autoTable(doc, {
    startY: finalY + 6,
    head: [["Total actif", "Total passif", "Equilibre", "Resultat net"]],
    body: [[totalActif, totalPassif, equilibre ? "Oui" : "Non", resultatNet]],
    headStyles: defaultPdfHeadStyle,
    styles: { fontSize: 8 },
  });

  addPdfFooter(doc);
  doc.save(buildFileName("bilan_comptable", dossierId, "pdf"));
};

/**
 * Grand Livre des tiers (clients / fournisseurs) : export Excel.
 * Une ligne par mouvement, regroupées par compte tiers trié en croissant.
 */
export const exportGrandLivreTiersExcel = ({
  tiers = [],
  dossierId,
  dateDebut,
  dateFin,
}) => {
  const rows = [];
  trierComptes(tiers, "compte").forEach((t) => {
    (t.mouvements || []).forEach((m) => {
      rows.push({
        "Type":            sanitizeExcelValue(t.type || ""),
        "Compte":          sanitizeExcelValue(t.compte || ""),
        "Tiers":           sanitizeExcelValue(t.nom || ""),
        "Date":            sanitizeExcelValue(m.date || ""),
        "Libelle":         sanitizeExcelValue(m.libelle || ""),
        "Contrepartie":    sanitizeExcelValue(m.contrepartie || ""),
        "Debit":           m.debit || 0,
        "Credit":          m.credit || 0,
        "Solde cumule":    m.solde_cumule || 0,
      });
    });
    // Ligne de total par tiers
    rows.push({
      "Type": "",
      "Compte": sanitizeExcelValue(t.compte || ""),
      "Tiers": "TOTAL " + (t.nom || ""),
      "Date": "", "Libelle": "", "Contrepartie": "",
      "Debit": t.total_debit || 0,
      "Credit": t.total_credit || 0,
      "Solde cumule":
        t.nature_solde === "D" ? (t.solde_debiteur || 0)
        : t.nature_solde === "C" ? -(t.solde_crediteur || 0)
        : 0,
    });
  });

  addExcelFooter(rows);

  const resume = trierComptes(tiers, "compte").map((t) => ({
    "Type":           sanitizeExcelValue(t.type || ""),
    "Compte":         sanitizeExcelValue(t.compte || ""),
    "Tiers":          sanitizeExcelValue(t.nom || ""),
    "Total debit":    t.total_debit || 0,
    "Total credit":   t.total_credit || 0,
    "Solde debiteur": t.solde_debiteur || 0,
    "Solde crediteur": t.solde_crediteur || 0,
    "Nature":         sanitizeExcelValue(t.nature_solde || "NUL"),
  }));

  let name = "grand_livre_tiers";
  if (dateDebut && dateFin) name += `_${dateDebut}_${dateFin}`;

  writeWorkbook(
    [
      { sheetName: "Grand Livre Tiers", rows },
      { sheetName: "Resume Tiers", rows: resume },
    ],
    buildFileName(name, dossierId),
  );
};
